import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Session } from '@supabase/supabase-js'
import {
  signUpWithEmail,
  signInWithEmail,
  signOut,
  getCurrentSession,
  createOrUpdateProfile,
  onAuthStateChange,
  hasRole
} from '@/services/auth'
import type { AuthUser } from '@/services/auth'
import { safeAuthStateLog, devLog } from '@/utils/logger'
import { supabase } from '@/services/supabase'

export const useUserStore = defineStore('user', () => {
  // State
  const user = ref<AuthUser | null>(null)
  const session = ref<Session | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false)
  const loggingOut = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!user.value && !!session.value)
  const isAdmin = computed(() => hasRole(user.value, 'admin'))
  const isTester = computed(() => hasRole(user.value, 'tester') || hasRole(user.value, 'admin'))
  const userName = computed(() => user.value?.name || 'Anonymous')
  const userAvatar = computed(() => user.value?.avatar_url || null)
  const userRole = computed(() => user.value?.user_role || 'user')
  const privacyMode = computed(() => user.value?.default_privacy_mode || 'public')
  const isochroneProfile = computed(() => user.value?.isochrone_profile || 'driving')
  const isochroneMinutes = computed(() => user.value?.isochrone_minutes || 15)

  // Actions
  async function signUp(email: string, password: string) {
    try {
      loading.value = true
      error.value = null

      const result = await signUpWithEmail(email, password)

      if (result.error) {
        throw new Error(result.error.message || 'Sign up failed')
      }

      // After successful signup, the auth state change listener will handle profile creation
      // For now, just return success

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Sign up failed'
      error.value = errorMessage
      console.error('Sign up error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  async function signIn(email: string, password: string) {
    try {
      loading.value = true
      error.value = null

      const result = await signInWithEmail(email, password)

      if (result.error) {
        throw new Error(result.error.message || 'Sign in failed')
      }

      // The actual user data will be set via the auth state change listener

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Sign in failed'
      error.value = errorMessage
      console.error('Sign in error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Signs out the current user and clears local state.
   *
   * Sets `loggingOut` flag before calling signOut() to prevent race conditions
   * with Supabase's autoRefreshToken. The flag stays true until HomeView clears it,
   * preventing unwanted redirects if a background token refresh completes after logout.
   *
   * **State Desync Handling:**
   * Detects when the UI shows authenticated but Supabase has no valid session.
   * In this case, skips the signOut API call and just clears local state.
   *
   * @throws Error - If signOut fails, the error is logged and re-thrown
   */
  async function logout() {
    console.log('[logout] Starting logout process...')
    console.log('[logout] Current state: user=', !!user.value, 'session=', !!session.value)

    try {
      loading.value = true
      error.value = null
      loggingOut.value = true  // Set BEFORE signOut to prevent race condition

      // Check for state desync - if session is already null/invalid, skip signOut
      const currentSession = await getCurrentSession()
      console.log('[logout] Checked current session:', !!currentSession)

      if (!currentSession) {
        console.warn('[logout] ⚠️ Session already null/invalid - clearing local state only')
        // Session is already gone, just clear local state
        user.value = null
        session.value = null
        console.log('[logout] Local state cleared (no signOut needed)')
        return
      }

      await signOut()
      console.log('[logout] signOut completed')

      // Clear local state
      user.value = null
      session.value = null
      console.log('[logout] Local state cleared')

    } catch (err) {
      console.error('[logout] Error during logout:', err)
      // Even on error, clear local state to ensure user can't stay stuck
      user.value = null
      session.value = null
      loggingOut.value = false  // Reset on error so user can retry
      const errorMessage = err instanceof Error ? err.message : 'Logout failed'
      error.value = errorMessage
      console.error('Logout error:', err)
      throw err
    } finally {
      loading.value = false
      // Note: loggingOut stays true until HomeView clears it
      console.log('[logout] Logout process complete')
    }
  }

  /**
   * Initialize authentication system by restoring session and subscribing to auth changes.
   *
   * This function performs one-time initialization of the authentication system for the user store.
   * It restores any existing session from storage, fetches the associated user profile, and sets up
   * a persistent listener for authentication state changes (sign-in, sign-out, token refresh, etc.).
   * The listener automatically updates the store state whenever authentication events occur.
   *
   * The function is idempotent - it will only run once per store instance (subsequent calls are ignored
   * if already initialized). It handles both initial page load scenarios (restoring existing sessions)
   * and runtime authentication events (email confirmation, OAuth callbacks, manual sign-in/out).
   *
   * **Error Handling & Defensive Auth State:**
   *
   * The auth state listener implements defensive error handling to prevent false "logged out" states:
   *
   * 1. **Profile Fetch Failures**: If `createOrUpdateProfile()` fails (RLS errors, network timeouts,
   *    profile not yet created), the listener preserves the previous `user.value` instead of setting
   *    it to `null`. This prevents the `isAuthenticated` computed property from returning `false`
   *    when a valid session exists.
   *
   * 2. **Session Validity Priority**: A valid session always takes precedence. Transient profile
   *    fetch errors (403 RLS, network errors) do NOT trigger logout - only explicit SIGNED_OUT
   *    events or null sessions clear the user state.
   *
   * 3. **Retry Logic**: The `createOrUpdateProfile` service function has built-in retry logic for
   *    "profile not found" errors (PGRST116), handling race conditions during email confirmation.
   *
   * 4. **State Consistency**: The listener ensures `session.value` and `user.value` remain in sync.
   *    If profile fetch fails on initial load (no previous user), `isAuthenticated` will correctly
   *    return `false` until profile fetch succeeds, preventing access to protected routes.
   *
   * @returns {Promise<void>} Resolves when initialization completes (session restored, listener active)
   * @throws {Error} May throw errors during session restoration or profile fetching, which are caught
   *                 internally and stored in the error state rather than propagated to callers
   *
   * @example
   * ```typescript
   * // In App.vue or main router guard
   * const userStore = useUserStore()
   * await userStore.initializeAuth()
   * ```
   */
  async function initializeAuth() {
    if (initialized.value) return

    try {
      loading.value = true
      error.value = null

      // Get current session
      const currentSession = await getCurrentSession()

      if (currentSession?.user) {
        session.value = currentSession

        // Create or update profile and get user data
        const userData = await createOrUpdateProfile(currentSession.user)
        user.value = userData
      }

      // Set up auth state change listener
      onAuthStateChange(async (event, newSession) => {
        safeAuthStateLog(event, newSession)

        // Skip processing if we're logging out - prevents race condition with token refresh
        if (loggingOut.value && event !== 'SIGNED_OUT') {
          devLog('🚫 Ignoring auth state change during logout:', event)
          return
        }

        session.value = newSession

        if (newSession?.user) {
          // Update profile for any auth event that includes a valid user session
          devLog('🔄 Fetching user profile...')
          const userData = await createOrUpdateProfile(newSession.user)

          if (userData) {
            // Profile fetch succeeded - update user state
            devLog('✅ Profile fetch successful')
            user.value = userData
            devLog(`Auth state: user=${!!user.value}, session=${!!session.value}, authenticated=${!!user.value && !!session.value}`)
          } else {
            // Profile fetch failed (RLS error, network error, etc.)
            // CRITICAL: Preserve previous user.value to prevent false logout state.
            // Session is still valid, so the user remains authenticated.
            // This handles transient failures without breaking isAuthenticated.
            console.warn('⚠️ Profile fetch failed but session is valid - preserving auth state')
            console.warn('⚠️ This may indicate: RLS policy issue, network error, or profile not yet created')

            // Keep existing user.value - do NOT set to null
            // If there's no previous user.value, we'll stay in a "session but no profile" state
            // which will show as not authenticated until profile fetch succeeds
          }
        } else if (event === 'SIGNED_OUT' || !newSession) {
          // User signed out or session cleared - definitive logout
          devLog('🚪 User signed out or session cleared')
          user.value = null
          loggingOut.value = false  // Clear the flag - logout is complete
        }
      })

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Auth initialization failed'
      error.value = errorMessage
      console.error('Auth initialization error:', err)
    } finally {
      initialized.value = true  // Always set - even on error
      loading.value = false
    }
  }

  async function updateProfile(updates: Partial<AuthUser>) {
    if (!user.value) return

    try {
      loading.value = true
      error.value = null

      // Update local state optimistically
      user.value = { ...user.value, ...updates }

      // TODO: Implement profile update API call when needed
      // This would involve calling Supabase to update the profiles table
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Profile update failed'
      error.value = errorMessage
      console.error('Profile update error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  function clearError() {
    error.value = null
  }

  /**
   * Updates the user's isochrone preferences in Supabase and local state.
   * Used for persisting transport mode and travel time preferences across sessions.
   *
   * @param profile - Transport mode: 'walking', 'cycling', or 'driving'
   * @param minutes - Travel time in minutes: 5, 10, 15, 20, or 30
   * @throws Logs error to console if database update fails
   */
  async function updateIsochronePreferences(
    profile: 'walking' | 'cycling' | 'driving',
    minutes: number
  ) {
    if (!user.value) return

    // Optimistic update - apply immediately for responsive UI
    user.value.isochrone_profile = profile
    user.value.isochrone_minutes = minutes

    // Persist to Supabase
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ isochrone_profile: profile, isochrone_minutes: minutes })
      .eq('user_id', user.value.id)

    if (updateError) {
      console.error('Failed to save isochrone preferences:', updateError)
      // Note: Optimistic update remains - user won't see error but preferences
      // may not persist across sessions until they're saved successfully
    }
  }

  // Initialize auth on store creation
  initializeAuth()

  return {
    // State
    user,
    session,
    loading,
    error,
    initialized,
    loggingOut,

    // Getters
    isAuthenticated,
    isAdmin,
    isTester,
    userName,
    userAvatar,
    userRole,
    privacyMode,
    isochroneProfile,
    isochroneMinutes,

    // Actions
    signUp,
    signIn,
    logout,
    initializeAuth,
    updateProfile,
    updateIsochronePreferences,
    clearError
  }
}) 